import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Checkbox, Divider, Table, TableBody,
  TableCell, TableHead, TableRow, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import { Settings } from '@mui/icons-material';
import {
  surgeryTypeService, surgeryTypeMappingService,
  consentFormTemplateService, preOpChecklistItemService,
} from '../../services/api';
import TopBar from '../../components/TopBar';

const SurgeryTypeMappingPage = () => {
  const navigate = useNavigate();
  const [types, setTypes] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [allChecklistItems, setAllChecklistItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
  const [selectedChecklistIds, setSelectedChecklistIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingMapping, setLoadingMapping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [typesRes, templatesRes, checklistRes] = await Promise.all([
        surgeryTypeService.getAll(),
        consentFormTemplateService.getAll(),
        preOpChecklistItemService.getAll(),
      ]);
      setTypes(typesRes.data.filter(t => t.isActive));
      setAllTemplates(templatesRes.data.filter(t => t.isActive));
      setAllChecklistItems(checklistRes.data.filter(i => i.isActive));
    } catch {
      alert('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openConfigure = async (type) => {
    setSelectedType(type);
    setLoadingMapping(true);
    setDialogOpen(true);
    try {
      const res = await surgeryTypeMappingService.get(type.id);
      if (res.data) {
        setSelectedTemplateIds((res.data.consentTemplates || []).map(t => t.id));
        setSelectedChecklistIds(res.data.checklistItemIds || []);
      } else {
        setSelectedTemplateIds([]);
        setSelectedChecklistIds([]);
      }
    } catch {
      setSelectedTemplateIds([]);
      setSelectedChecklistIds([]);
    } finally {
      setLoadingMapping(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await surgeryTypeMappingService.upsert(selectedType.id, {
        consentTemplateIds: selectedTemplateIds,
        checklistItemIds: selectedChecklistIds,
      });
      setDialogOpen(false);
      load();
    } catch {
      alert('Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const toggleTemplate = (id) =>
    setSelectedTemplateIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleChecklist = (id) =>
    setSelectedChecklistIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Surgery Type Mapping" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Configure which <strong>consent forms</strong> and <strong>pre-op checklist items</strong> are automatically created when a surgery of that type is scheduled.
            Manage checklist items in <strong>Pre-Op Checklist Items</strong>.
          </Typography>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Surgery Type</strong></TableCell>
                  <TableCell><strong>Consent Forms</strong></TableCell>
                  <TableCell><strong>Pre-op Items</strong></TableCell>
                  <TableCell width={120}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {types.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No active surgery types found. Add surgery types first.
                    </TableCell>
                  </TableRow>
                )}
                {types.map(type => (
                  <TableRow key={type.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{type.name}</Typography>
                      {type.description && <Typography variant="caption" color="text.secondary">{type.description}</Typography>}
                    </TableCell>
                    <TableCell>
                      {type.consentTemplateCount > 0
                        ? <Chip size="small" color="primary" variant="outlined" label={`${type.consentTemplateCount} form${type.consentTemplateCount > 1 ? 's' : ''}`} />
                        : <Chip size="small" color="default" variant="outlined" label="Not set" />}
                    </TableCell>
                    <TableCell>
                      {type.checklistItemCount > 0
                        ? <Chip size="small" color="success" variant="outlined" label={`${type.checklistItemCount} item${type.checklistItemCount > 1 ? 's' : ''}`} />
                        : <Chip size="small" color="default" variant="outlined" label="Not set" />}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Configure Mapping">
                        <Button size="small" variant="outlined" startIcon={<Settings />} onClick={() => openConfigure(type)}>
                          Configure
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Container>

      {/* Mapping Configuration Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Configure Mapping — <strong>{selectedType?.name}</strong>
        </DialogTitle>
        <DialogContent>
          {loadingMapping ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              {/* Consent Forms */}
              <Divider sx={{ mb: 1 }}>
                <Chip
                  label={`Consent Forms  (${selectedTemplateIds.length} selected)`}
                  size="small"
                  color={selectedTemplateIds.length > 0 ? 'primary' : 'default'}
                />
              </Divider>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Selected consent forms will be auto-created (Pending status) when a surgery of this type is scheduled.
                Manage templates in <strong>Consent Form Templates</strong>.
              </Typography>
              <Box sx={{ pl: 1, mb: 2 }}>
                {allTemplates.length === 0 && (
                  <Typography variant="caption" color="text.secondary">No active templates found.</Typography>
                )}
                {allTemplates.map(tmpl => (
                  <Box key={tmpl.id} sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTemplateIds.includes(tmpl.id)}
                          onChange={() => toggleTemplate(tmpl.id)}
                          size="small" color="primary"
                        />
                      }
                      label={tmpl.name}
                    />
                  </Box>
                ))}
              </Box>

              {/* Pre-op Checklist Items */}
              <Divider sx={{ mb: 1 }}>
                <Chip
                  label={`Pre-op Checklist  (${selectedChecklistIds.length} selected)`}
                  size="small"
                  color={selectedChecklistIds.length > 0 ? 'success' : 'default'}
                />
              </Divider>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Selected items will appear on the pre-op checklist tab for surgeries of this type.
                Add new items in <strong>Pre-Op Checklist Items</strong>.
              </Typography>
              <Box sx={{ pl: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                {allChecklistItems.length === 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1/-1' }}>
                    No active checklist items found.
                  </Typography>
                )}
                {allChecklistItems.map(item => (
                  <FormControlLabel
                    key={item.id}
                    control={
                      <Checkbox
                        checked={selectedChecklistIds.includes(item.id)}
                        onChange={() => toggleChecklist(item.id)}
                        size="small" color="success"
                      />
                    }
                    label={<Typography variant="body2">{item.name}</Typography>}
                  />
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || loadingMapping}>
            {saving ? 'Saving…' : 'Save Mapping'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SurgeryTypeMappingPage;
